import { useEffect, useState } from "react";
import { insightsApi, propertyApi, type PropertyDto } from "../api.js";
import { Card, EmptyState, ErrorBanner, Money, PageHeader, Spinner } from "../components/ui.js";

interface PerformanceDto {
  propertyId: string;
  totalIncome: number;
  totalExpenses: number;
  netCashflow: number;
  yieldPercent: number;
  monthCount: number;
}
interface HealthDto { healthScore: number; healthLabel: string; }
export interface PerformanceRow extends PerformanceDto {
  propertyName: string;
  currency?: string;
}

export function buildPerformanceRows(performance: PerformanceDto[], properties: PropertyDto[]): PerformanceRow[] {
  const propertyById = new Map(properties.map((property) => [property.id, property]));
  return performance.map((item) => {
    const property = propertyById.get(item.propertyId);
    return {
      ...item,
      propertyName: property?.name ?? "Property",
      currency: property?.currency,
    };
  });
}

export function InsightsPage() {
  const [health, setHealth] = useState<HealthDto | null>(null);
  const [perf, setPerf] = useState<PerformanceDto[] | null>(null);
  const [properties, setProperties] = useState<PropertyDto[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      const [h, p, propertyResult] = await Promise.all([
        insightsApi.healthPortfolio(),
        insightsApi.performance(),
        propertyApi.list(),
      ]);
      if (!h.ok || !h.data) {
        setError(h.error?.errorCode ?? "Insights unavailable");
        return;
      }
      if (!p.ok) {
        setError(p.error?.errorCode ?? "Property performance unavailable");
        return;
      }
      if (!propertyResult.ok) {
        setError(propertyResult.error?.errorCode ?? "Property details unavailable");
        return;
      }
      setHealth(h.data as HealthDto);
      setPerf(p.data as PerformanceDto[] ?? []);
      setProperties(propertyResult.data ?? []);
    })();
  }, []);

  if (error) return <ErrorBanner message={error} />;
  if (!health) return <Spinner />;

  return (
    <div className="page-stack">
      <PageHeader
        eyebrow="Performance"
        title="Insights"
        description="Understand portfolio health and compare the performance data Nettstay can currently prove."
      />
      <Card title="Portfolio health" description="A simple signal based on your recorded portfolio information.">
        <div className="health-display">
          <span className="health-score">{health.healthScore}</span>
          <span className="health-label">{health.healthLabel}</span>
        </div>
      </Card>
      <Card title="Property performance" description="Compare occupancy and financial movement across your properties.">
        {perf === null ? <Spinner /> : perf.length === 0 ? <EmptyState title="No performance data yet" message="Add a property and record financial activity to build this view." /> : (
          <div className="table-shell"><table className="data-table">
            <thead><tr><th>Property</th><th>Yield</th><th>Income</th><th>Expenses</th><th>Net cashflow</th></tr></thead>
            <tbody>
              {buildPerformanceRows(perf, properties).map((p) => (
                <tr key={p.propertyId}>
                  <td>{p.propertyName}</td>
                  <td>{p.yieldPercent.toFixed(1)}%</td>
                  <td><Money amount={p.totalIncome} currency={p.currency} /></td>
                  <td><Money amount={p.totalExpenses} currency={p.currency} /></td>
                  <td><Money amount={p.netCashflow} currency={p.currency} /></td>
                </tr>
              ))}
            </tbody>
          </table></div>
        )}
      </Card>
    </div>
  );
}
